<template>
	<SimulatorSheet :stepNum="stepNum" :stepName="stepName">
		<div
			:style="{
				marginLeft:
					'calc( -1 * var(--padding-left-sheet-content) + var(--padding-right-sheet-header) )',
			}"
			v-if="simulation"
		>
			<div
				class="simulator__grid simulator__grid--1-1-1 mb-80"
				v-if="['PENDING'].includes(simulation.calculationStatus)"
			>
				<div></div>
				<div>
					<p class="fsz-xl fw-light text-balance color-neutral-700 mb-16">
						{{ $t("Já terminou a sua simulação?") }}
					</p>
					<p class="fsz-l fw-light text-balance color-neutral-700">
						{{
							$t(
								"Clique em 'Iniciar cálculo' para ver os resultados ou volte atrás para continuar a editar a sua simulação."
							)
						}}
					</p>
				</div>
				<div>
					<BBtn @click="handleCalculateBtnClick" size="l">{{
						$t("Iniciar cálculo")
					}}</BBtn>
				</div>
			</div>

			<div
				class="simulator__grid simulator__grid--1-1-1 mb-80"
				v-else-if="['IN_PROGRESS'].includes(simulation.calculationStatus)"
			>
				<div></div>
				<p class="fsz-xl fw-light text-balance color-neutral-700">
					{{ $t("Aguarde, o seu cálculo está a ser efetuado") }}
				</p>
			</div>

			<div
				class="simulator__grid simulator__grid--1-1-1 mb-80"
				v-else-if="['ERROR'].includes(simulation.calculationStatus)"
			>
				<div></div>
				<p class="fsz-xl fw-light text-balance color-neutral-700">
					{{ $t("Ocorreu um erro ao calcular. Tente novamente mais tarde.") }}
				</p>
			</div>

			<div class="simulator__grid simulator__grid--1-1-1 mb-80" v-else>
				<div></div>
				<p
					class="color-neutral-600"
					v-html="
						$t(
							'Encontrámos ${totalResultsHighlight} que correspondem aos seus critérios de simulação. Seleccione o produto que melhor se adequar ao seu projecto.',
							{
								totalResultsHighlight: `<span class='color-neutral-900'>
									${$t('${totalResults} resultados', {
										totalResults: Array.isArray(simulation.result)
											? simulation.result.length
											: 0,
									})}
								</span>`,
							}
						)
					"
				></p>
			</div>

			<div class="mb-60">
				<SimulationCard
					:summary="{
						...simulationSummary,
						calculationStatus: simulation.calculationStatus,
					}"
					:dumb="true"
				/>

				<div
					v-if="simulation.calculationStatus === 'COMPLETE'"
					class="results mt-60"
				>
					<div class="results__tableToolbar">
						<div class="results__filtersResetBlock">
							<p class="results__totalAppliedFilters">
								{{
									$t("${totalAppliedFilters} Filtros aplicados", {
										totalAppliedFilters,
									})
								}}
							</p>
							<div>
								<span
									@click="handleResetFiltersClick"
									class="results__resetBtn"
									>{{ $t("Reset") }}</span
								>
							</div>
						</div>

						<div class="results__filters">
							<div>
								<div class="results__filterLabel">{{ $t("Secção") }}</div>
								<div>
									<BRangeSlider
										:style="{ width: '150px' }"
										v-model="section"
										:min="filtersCfg.section.limits[0]"
										:max="filtersCfg.section.limits[1]"
										:step="filtersCfg.section.step"
									/>
								</div>
							</div>

							<div>
								<div class="results__filterLabel">
									{{ $t("Espessura") }} (mm)
								</div>
								<div>
									<BRangeSlider
										:style="{ width: '150px' }"
										v-model="width"
										:min="filtersCfg.width.limits[0]"
										:max="filtersCfg.width.limits[1]"
										:step="filtersCfg.width.step"
									/>
								</div>
							</div>
							<div>
								<div class="results__filterLabel">{{ $t("Aço") }}</div>
								<div class="flex flex-wrap" :style="{ gap: '10px' }">
									<BToggle
										v-for="item in simulationOptions.steel.options"
										:key="item.id"
										:value="steelGrades.includes(item.id)"
										@input="() => handleToggleSteel(item.id)"
										:label="item.label"
										size="s"
									/>
								</div>
							</div>

							<div>
								<div class="results__filterLabel">
									{{ $t("Incluir todas as soluções") }}
									<span
										class="results__filterLabel__tooltipIcon"
										v-tooltip.auto-start="{
											container: '.simulatorSheets',
											content: $t(
												'Por defeito, é exibida apenas uma solução de cada tipologia de perfil.'
											),
											delay: {
												show: 50,
												hide: 200,
											},
										}"
									>
										?
									</span>
								</div>
								<div class="flex flex-wrap" :style="{ gap: '10px' }">
									<BSwitch
										:label-off="$t('Não')"
										:label-on="$t('Sim')"
										v-model="isAllSolutionsShown"
										size="s"
									/>
								</div>
							</div>
						</div>
					</div>

					<div class="results__table">
						<div class="results__tableHead results__tableGrid">
							<div class="results__tableValuesGrid">
								<div>{{ $t("Perfil") }}</div>
								<div>{{ $t("Altura") }} (mm)</div>
								<div>{{ $t("Espessura") }} (mm)</div>
								<div>{{ $t("Comp alinhamento") }} (mm)</div>
								<div>{{ $t("Peso alinhamento") }} (Kg)</div>
								<div>{{ $t("Peso estimado") }} (Kg/m2)</div>
							</div>
						</div>

						<div v-if="displayedSolutions.length" class="results__tableBody">
							<div
								class="results__tableRow results__tableGrid"
								:class="{
									'is-selected': chosenSolution === item.id,
								}"
								v-for="(item, idx) in displayedSolutions"
								:key="idx"
								@click="() => handleSolutionRowClick(item)"
							>
								<div class="results__tableValuesGrid">
									<div class="results__itemProfile">
										<div class="results__itemProfileName">
											{{ item.sectionType }} {{ item.sectionName }}
											{{ item.steelGrade }}
										</div>
										<div class="results__itemProfileType">
											<!-- Perfil com continuidade natural -->
											Perfil {{ item.typology }}
										</div>
									</div>

									<div class="results__itemHeight">
										<p class="results__tableShortScreenHeadLabel">
											{{ $t("Altura") }} (mm)
										</p>
										<p>{{ item.formatted.sectionHeightMM }}</p>
									</div>
									<div class="results__itemWidth">
										<p class="results__tableShortScreenHeadLabel">
											{{ $t("Espessura") }} (mm)
										</p>
										<p>{{ item.formatted.sectionThicknessMM }}</p>
									</div>
									<div class="results__itemLengthAlignment">
										<p class="results__tableShortScreenHeadLabel">
											{{ $t("Comp alinhamento") }} (mm)
										</p>
										<p>{{ item.formatted.totalLengthM }}</p>
									</div>
									<div class="results__itemWeight">
										<p class="results__tableShortScreenHeadLabel">
											{{ $t("Peso alinhamento") }} (Kg)
										</p>
										<p>{{ item.formatted.totalWeightKg }}</p>
									</div>
									<div class="results__itemEstimatedWeight">
										<p class="results__tableShortScreenHeadLabel">
											{{ $t("Peso estimado") }} (Kg/m2)
										</p>
										<p>{{ item.formatted.totalWeightKgPerSqm }}</p>
									</div>
								</div>

								<div v-if="isGraphicsMode" class="results__itemGraphic">
									<div class="results__viewControls">
										<BSelectStepper
											class="results__zoomStepper"
											v-model="item.zoom"
											:options="zoomOptions"
										/>

										<div class="results__expandBtn">
											<BIcon
												@click="() => handleExpandItemGraphic(item)"
												name="Maximize"
												size="20px"
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div v-else>
							<p class="results__noResults">
								{{
									$t(
										"Não foram encontrados resultados para os filtros aplicados."
									)
								}}
							</p>
						</div>
					</div>
				</div>
			</div>

			<div class="sheet__footer">
				<BBtn
					@click="handleDownloadBtnClick"
					:disabled="!isDownloadBtnEnabled"
					size="l"
					variant="text"
					>{{ $t("Nota de cálculo") }}</BBtn
				>
			</div>
		</div>

		<portal to="root">
			<SimulatorMetaDetailsModal
				v-if="isSimulationMetaDetailsModalOpen"
				@close="() => (isSimulationMetaDetailsModalOpen = false)"
			/>
		</portal>
	</SimulatorSheet>
</template>

<script>
import { mapActions, mapGetters, mapState, mapMutations } from "vuex";
import { formatDate, formatTime, isNil, groupBy } from "@/utils";

import SimulatorMetaDetailsModal from "./SimulatorMetaDetailsModal.vue";

import SimulatorSheet from "./SimulatorSheet.vue";

import BSelector from "@/components/base/BSelector.vue";
import BSelect from "@/components/base/BSelect.vue";
import BFieldBlock from "@/components/base/BFieldBlock.vue";
import BCheckbox from "@/components/base/BCheckbox.vue";
import BFieldNumber from "@/components/base/BFieldNumber.vue";
import BBtn from "@/components/base/BBtn.vue";
import BToggle from "@/components/base/BToggle.vue";
import StatusBadge from "@/components/StatusBadge.vue";
import BRangeSlider from "@/components/base/BRangeSlider.vue";
import BSwitch from "@/components/base/BSwitch.vue";
import BIcon from "@/components/base/BIcon";
import BSelectStepper from "@/components/base/BSelectStepper.vue";

import mixinEmitLifecycleEvents from "@/mixins/emitLifecycleEvents";
import BFieldText from "../../components/base/BFieldText.vue";

import simulationOptions from "@/store/modules/simulations/models/simulationOptions";
import SimulationCard from "@/components/SimulationCard";

export default {
	components: {
		BSelector,
		BToggle,
		BFieldBlock,
		BCheckbox,
		BSelect,
		BFieldNumber,
		BBtn,
		StatusBadge,
		BRangeSlider,
		BSwitch,
		BIcon,
		BSelectStepper,
		BFieldText,
		SimulatorMetaDetailsModal,
		SimulatorSheet,
		SimulationCard,
	},
	mixins: [mixinEmitLifecycleEvents],
	props: {
		...SimulatorSheet.props,
	},
	data() {
		const filtersCfg = {
			section: {
				limits: [50, 300],
				step: 50,
			},
			width: {
				limits: [1.5, 3],
				step: 0.5,
			},
			steel: {
				...simulationOptions.steel,
			},
		};

		return {
			filtersCfg,

			section: [...filtersCfg.section.limits],
			width: [...filtersCfg.width.limits],

			isGraphicsMode: false,

			zoomOptions: [25, 50, 75, 100].map((item) => {
				return {
					id: item / 100,
					label: `${item}%`,
				};
			}),

			isSimulationMetaDetailsModalOpen: false,
			chosenSolution: null,
			steelGrades: [],
			isAllSolutionsShown: false,
		};
	},
	computed: {
		...mapState("simulator", {
			simulationOptions: "simulationOptions",
		}),

		...mapGetters("simulator", {
			simulation: "originalSimulation",
			spanDescriptions: "spanDescriptions",
		}),

		...mapGetters("simulations", ["getSimulationSummary"]),

		simulationSummary() {
			return this.getSimulationSummary(this.simulation?.id);
		},

		totalAppliedFilters() {
			const isSectionFilterApplied =
				this.section[0] !== this.filtersCfg.section.limits[0] ||
				this.section[1] !== this.filtersCfg.section.limits[1];

			const isWidthFilterApplied =
				this.width[0] !== this.filtersCfg.width.limits[0] ||
				this.width[1] !== this.filtersCfg.width.limits[1];

			const isSteelFilterApplied =
				this.steelGrades.length !== this.filtersCfg.steel.options.length;

			return [
				isSectionFilterApplied,
				isWidthFilterApplied,
				isSteelFilterApplied,
			].filter(Boolean).length;
		},

		isDownloadBtnEnabled() {
			return !isNil(this.chosenSolution);
		},

		solutions() {
			return this.simulation?.result?.map((item) => {
				return {
					...item,
					formatted: {
						sectionHeightMM: this.formatNum(item.sectionHeightMM),
						sectionThicknessMM: this.formatNum(item.sectionThicknessMM),
						totalLengthM: this.formatNum(item.totalLengthM),
						totalWeightKg: this.formatNum(item.totalWeightKg),
						totalWeightKgPerSqm: this.formatNum(item.totalWeightKgPerSqm),
					},
					zoom: 1,
				};
			});
		},

		sortedSolutions() {
			if (!this.solutions?.length) {
				return [];
			}

			const byTypology = groupBy(this.solutions, "typology");
			const byTypologyValues = Object.values(byTypology);

			return byTypologyValues.flatMap((solutions) => {
				return solutions.sort(
					(a, b) => a.totalWeightKgPerSqm - b.totalWeightKgPerSqm
				);
			});
		},

		filteredSolutions() {
			return this.sortedSolutions?.filter((item) => {
				const isSectionInRange =
					item.sectionHeightMM >= this.section[0] &&
					item.sectionHeightMM <= this.section[1];

				const isWidthInRange =
					item.sectionThicknessMM >= this.width[0] &&
					item.sectionThicknessMM <= this.width[1];

				const isSteelSelected = this.steelGrades.includes(item.steelGrade);

				return isSectionInRange && isWidthInRange && isSteelSelected;
			});
		},

		displayedSolutions() {
			if (this.isAllSolutionsShown) {
				return this.filteredSolutions;
			}

			return this.filteredSolutions?.reduce((displayedSolutions, solution) => {
				const isTypologyAlreadyDisplayed = displayedSolutions.some(
					(item) => item.typology === solution.typology
				);

				if (isTypologyAlreadyDisplayed) {
					return displayedSolutions;
				}

				return [...displayedSolutions, solution];
			}, []);
		},
	},

	methods: {
		...mapActions("simulator", ["initCalculation"]),
		...mapActions("simulations", [
			"downloadCalculationNote",
			"updateSimulationMetadata",
		]),

		formatDate,
		formatTime,

		formatNum(num) {
			return num.toFixed(2);
		},

		resetFilters() {
			this.section = [...this.filtersCfg.section.limits];
			this.width = [...this.filtersCfg.width.limits];
			this.steelGrades = [
				...this.filtersCfg.steel.options.map((item) => item.id),
			];
		},

		handleResetFiltersClick() {
			this.resetFilters();
		},

		handleToggleSteel(steelId) {
			if (this.steelGrades.includes(steelId)) {
				this.steelGrades = this.steelGrades.filter((item) => item !== steelId);
			} else {
				this.steelGrades.push(steelId);
			}
		},

		handleCalculateBtnClick() {
			this.initCalculation();
		},

		handleSolutionRowClick(solution) {
			this.chosenSolution = solution.id;
		},

		handleDownloadBtnClick() {
			this.$NProgress.start();

			this.updateSimulationMetadata({
				id: this.simulation.id,
				chosenResultId: this.chosenSolution,
				steelGrades: this.steelGrades,
			})
				.then(() => {
					return this.downloadCalculationNote({ id: this.simulation.id });
				})
				.catch((err) => {
					const originalMsg =
						err?.response?.data?.message ?? err.message ?? err;

					const defaultMsg = this.$t(
						"Falhou o download da nota de cálculo. Por favor tente novamente."
					);

					this.$notify({
						type: "error",
						title: originalMsg ?? defaultMsg,
					});
				})
				.finally(() => {
					this.$NProgress.done();
				});
		},

		handleExpandItemGraphic() {},
	},

	created() {
		this.chosenSolution = this.simulation.chosenSolution;
		this.steelGrades = this.simulation.steelGrades;

		if (this.simulation.chosenSolution) {
			const isChosenSolutionInDisplayedSolutions = this.displayedSolutions.some(
				({ id }) => id === this.chosenSolution
			);

			if (!isChosenSolutionInDisplayedSolutions) {
				this.isAllSolutionsShown = true;
			}
		}
	},
};
</script>

<style lang="scss" scoped>
.results__tableToolbar {
	display: grid;
	grid-template-columns: 1fr 2fr;
	margin-bottom: 50px;
}

.results__filters {
	display: flex;
	column-gap: 50px;
	flex-wrap: wrap;
	row-gap: 30px;
}

.results__filtersResetBlock {
	padding-right: 20px;
}

.results__filterLabel {
	margin-bottom: 15px;
}

.results__totalAppliedFilters {
	margin-bottom: 23px;
}

.results__resetBtn {
	color: var(--color-neutral-200);
	cursor: pointer;
	font-size: var(--font-size-xs);
}

.results__table {
	--padding-inline: 20px;
}

.results__tableGrid {
}

.results__tableValuesGrid {
	display: grid;
	grid-template-columns: 333px 1fr 1fr 1fr 1fr 1fr;
	gap: 20px;

	& > :first-child {
		padding-right: 60px;
	}
}

.results__tableHead {
	color: var(--color-neutral-600);
	font-size: var(--font-size-xs);
	margin-bottom: 30px;
	padding-inline: var(--padding-inline);
}

.results__tableShortScreenHeadLabel {
	display: none;
	color: var(--color-neutral-600);
	font-size: var(--font-size-xs);
}

@media (max-width: 900px) {
	.results__tableValuesGrid {
		grid-template-columns: 1fr 1fr 1fr;
	}

	.results__tableHead {
		display: none;
	}

	.results__tableShortScreenHeadLabel {
		display: block;
	}
}

@media (max-width: 700px) {
	.results__tableValuesGrid {
		grid-template-columns: 1fr 1fr;
	}
}

@media (max-width: 500px) {
	.results__tableValuesGrid {
		grid-template-columns: 1fr;
	}
}

.results__tableRow {
	border-radius: 8px;
	background: var(--color-neutral-0);
	padding: var(--padding-inline);
	cursor: pointer;

	&:not(:last-child) {
		margin-bottom: 2px;
	}

	&:hover {
		box-shadow: inset 0 0 0 2px var(--color-neutral-200);
	}

	&.is-selected {
		box-shadow: inset 0 0 0 2px var(--color-primary-600);
	}
}

.results__itemProfileType {
	color: var(--color-neutral-600);
	font-size: var(--font-size-xs);
}

.results__itemGraphic {
	position: relative;
	height: 160px;
	border-radius: 8px;
	background: var(--color-neutral-50);
	margin-top: 30px;
}

.results__viewControls {
	--offset: 1rem;
	position: absolute;
	bottom: var(--offset);
	right: var(--offset);
	display: flex;
	gap: 15px;
}

.results__expandBtn {
	--border-radius: 8px;
	background-color: var(--color-neutral-700);
	color: #fff;
	border-radius: var(--border-radius);
	display: grid;
	place-content: center;
	width: 50px;
}

.results__noResults {
	border-radius: 8px;
	background: var(--color-neutral-0);
	padding: 27px var(--padding-inline);
	text-align: center;
	color: var(--color-neutral-600);
}

.results__filterLabel__tooltipIcon {
	color: #fff;
	vertical-align: top;
	font-size: 0.6em;
	display: inline-flex;
	width: 1.2em;
	height: 1.2em;
	background: var(--color-neutral-900);
	align-items: center;
	justify-content: center;
	border-radius: 50%;
}
</style>
